// Checkout service for final order submission

import { api } from '@/api/client';
import { getSessionId, track } from '@/utils/analytics';
import { logger } from '@/utils/logger';
import { FILE_UPLOAD_LIMITS } from '@/utils/constants';
import type { CheckoutStep, CheckoutData, CheckoutFiles, CheckoutResult, OrderStatusRequest, OrderStatusResponse } from '@/types';

export async function getCheckoutSteps(userId: string): Promise<CheckoutStep[]> {
  const response = await api.get<{ steps: CheckoutStep[] }>(
    '/api/2turniton/checkout/steps',
    { userId }
  );

  if (response.success && response.data) {
    return response.data.steps || [];
  }

  return [];
}

const CHECKOUT_REQUEST_TIMEOUT_MS = 90_000;
const MAX_UPLOAD_ATTEMPTS = 3;
// Backoff before attempt 2 and attempt 3. Short, because the user is watching.
const RETRY_BACKOFF_MS = [1_500, 4_000];
// Hard wall-clock ceiling on the whole submit, retries included. Without this,
// three 90s attempts plus backoff could hold the user for ~4.5 minutes, which is
// worse than the dropped upload we are retrying around.
const MAX_TOTAL_UPLOAD_MS = 120_000;

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
  /** 1-based attempt number, so the UI can say "retrying". */
  attempt: number;
}

type UploadOutcome =
  | { kind: 'response'; status: number; body: string }
  // `bodyFullySent` is the whole reason we use XHR instead of fetch: it tells us
  // whether the server could possibly have received a complete request.
  | { kind: 'network'; bodyFullySent: boolean }
  | { kind: 'timeout'; bodyFullySent: boolean };

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Sends one checkout attempt over XHR.
 *
 * fetch() cannot tell us how much of the request body made it out before a
 * connection dropped, which makes every failure ambiguous and every retry a
 * potential duplicate order. XHR reports upload progress, so we can distinguish
 * "the upload died mid-flight, the server never saw a complete body" (safe to
 * retry) from "the body went out but the response was lost" (never retry).
 */
function sendCheckoutRequest(
  body: FormData,
  userId: string,
  sessionId: string | null,
  attempt: number,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadOutcome> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let bodyFullySent = false;
    let settled = false;

    const settle = (outcome: UploadOutcome) => {
      if (settled) return;
      settled = true;
      resolve(outcome);
    };

    xhr.open('POST', '/api/2turniton/checkout/complete');
    xhr.timeout = CHECKOUT_REQUEST_TIMEOUT_MS;
    xhr.setRequestHeader('x-user-id', userId);
    if (sessionId) {
      xhr.setRequestHeader('x-client-session-id', sessionId);
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: event.total > 0 ? Math.min(100, Math.round((event.loaded / event.total) * 100)) : 0,
        attempt,
      });
    });

    // Fires once the entire request body has been handed off to the network.
    xhr.upload.addEventListener('load', () => {
      bodyFullySent = true;
    });

    xhr.addEventListener('load', () => {
      settle({ kind: 'response', status: xhr.status, body: xhr.responseText ?? '' });
    });
    xhr.addEventListener('error', () => settle({ kind: 'network', bodyFullySent }));
    xhr.addEventListener('abort', () => settle({ kind: 'network', bodyFullySent }));
    xhr.addEventListener('timeout', () => settle({ kind: 'timeout', bodyFullySent }));

    xhr.send(body);
  });
}

export async function submitCheckout(
  userId: string,
  checkoutData: CheckoutData,
  files: CheckoutFiles,
  onProgress?: (progress: UploadProgress) => void
): Promise<CheckoutResult> {
  const startTime = Date.now();
  const sessionId = getSessionId();
  const fileSizes = {
    dlFile: files.dlFile?.size ?? null,
    rentFile: files.rentFile?.size ?? null,
    ownFile: files.ownFile?.size ?? null,
  };
  const totalFileSize = Object.values(fileSizes).reduce((sum: number, s) => sum + (s ?? 0), 0);

  // Pre-flight size guard: reject before making network request
  const maxCombinedBytes = FILE_UPLOAD_LIMITS.MAX_COMBINED_FILES_MB * 1024 * 1024;
  if (totalFileSize > maxCombinedBytes) {
    const totalMB = (totalFileSize / (1024 * 1024)).toFixed(1);
    logger.warn({ area: 'checkout' }, `Pre-flight size check failed: ${totalMB} MB exceeds ${FILE_UPLOAD_LIMITS.MAX_COMBINED_FILES_MB} MB limit`);
    track('checkout_api_error', {
      user_id: userId,
      error_message: 'Documents too large (pre-flight)',
      error_type: 'size_limit',
      file_sizes: fileSizes,
      total_file_size: totalFileSize,
      session_id: sessionId,
    });
    return {
      success: false,
      error: `Your documents total ${totalMB} MB, which exceeds the ${FILE_UPLOAD_LIMITS.MAX_COMBINED_FILES_MB} MB upload limit. Please use smaller files.`,
    };
  }

  // Rebuilt per attempt — a FormData that has already been sent should not be reused.
  const buildFormData = () => {
    const formData = new FormData();
    formData.append('data', JSON.stringify(checkoutData));
    if (files.dlFile) {
      formData.append('dlFile', files.dlFile);
    }
    if (files.rentFile) {
      formData.append('rentFile', files.rentFile);
    }
    if (files.ownFile) {
      formData.append('ownFile', files.ownFile);
    }
    return formData;
  };

  const onlineStatus = () => (
    typeof navigator === 'undefined' ? 'unknown' : navigator.onLine ? 'online' : 'offline'
  );
  const baseErrorProps = () => ({
    user_id: userId,
    duration_ms: Date.now() - startTime,
    file_sizes: fileSizes,
    total_file_size: totalFileSize,
    had_files: { dl: !!files.dlFile, rent: !!files.rentFile, own: !!files.ownFile },
    checkout_email: checkoutData.email,
    service_address_zip: checkoutData.serviceZip,
    session_id: sessionId,
  });

  logger.info(
    { area: 'checkout' },
    `Submitting checkout with ${(totalFileSize / (1024 * 1024)).toFixed(1)} MB of files`
  );

  for (let attempt = 1; attempt <= MAX_UPLOAD_ATTEMPTS; attempt++) {
    let outcome: UploadOutcome;
    try {
      outcome = await sendCheckoutRequest(buildFormData(), userId, sessionId, attempt, onProgress);
    } catch (err) {
      // sendCheckoutRequest only rejects if XHR itself could not be started.
      logger.error({ area: 'checkout' }, 'Checkout request could not be started', err instanceof Error ? err : undefined);
      track('checkout_api_error', {
        ...baseErrorProps(),
        error_message: err instanceof Error ? err.message : String(err),
        error_type: 'exception',
        error_name: err instanceof Error ? err.name : 'Unknown',
        attempt,
        online_status: onlineStatus(),
      });
      return { success: false, error: 'We could not start the upload. Please try again.' };
    }

    // --- Connection died before or during the upload ---
    if (outcome.kind === 'network' || outcome.kind === 'timeout') {
      const isTimeout = outcome.kind === 'timeout';

      // Retry only when all three hold:
      //  1. The body never finished going out, so the server cannot have processed
      //     a complete request and a retry cannot duplicate an order.
      //  2. It was a dropped connection, not a timeout. A timeout means we already
      //     spent the full 90s budget; restarting the upload from zero would just
      //     burn another 90s on a connection we know is too slow.
      //  3. We are still inside the overall time budget.
      const canRetrySafely =
        !outcome.bodyFullySent &&
        !isTimeout &&
        attempt < MAX_UPLOAD_ATTEMPTS &&
        Date.now() - startTime < MAX_TOTAL_UPLOAD_MS;

      if (canRetrySafely) {
        const backoff = RETRY_BACKOFF_MS[attempt - 1] ?? 4_000;
        logger.warn(
          { area: 'checkout' },
          `Upload dropped mid-flight on attempt ${attempt} (${isTimeout ? 'timeout' : 'network'}), retrying in ${backoff}ms`
        );
        track('checkout_upload_retry', {
          ...baseErrorProps(),
          attempt,
          error_type: isTimeout ? 'timeout' : 'network',
          online_status: onlineStatus(),
        });
        await delay(backoff);
        continue;
      }

      // Body fully sent but no response: the order may exist. Never invite a retry.
      if (outcome.bodyFullySent) {
        const errorMessage = isTimeout
          ? `Your documents finished uploading but we did not get a confirmation back in time.`
          : `Your documents finished uploading but the connection dropped before we got a confirmation.`;
        logger.error({ area: 'checkout' }, `Checkout response lost after full upload (${outcome.kind})`);
        track('checkout_api_error', {
          ...baseErrorProps(),
          error_message: errorMessage,
          error_type: isTimeout ? 'timeout_response_lost' : 'network_response_lost',
          attempt,
          online_status: onlineStatus(),
        });
        return {
          success: false,
          mayHaveSucceeded: true,
          error: `${errorMessage} Your order may already be submitted — please do not submit again. Call 1-888-300-6961 and we will confirm it for you.`,
        };
      }

      // Out of retries with the upload still failing to get through.
      logger.error({ area: 'checkout' }, `Checkout upload failed after ${attempt} attempts (${outcome.kind})`);
      track('checkout_api_error', {
        ...baseErrorProps(),
        error_message: isTimeout
          ? `Request timed out after ${CHECKOUT_REQUEST_TIMEOUT_MS / 1000}s`
          : 'Upload connection dropped before the request completed',
        error_type: isTimeout ? 'timeout' : 'network',
        attempt,
        attempts_used: attempt,
        online_status: onlineStatus(),
      });
      if (onlineStatus() === 'offline') {
        return {
          success: false,
          error: 'You appear to be offline. Reconnect to the internet and tap Submit again — nothing has been sent yet.',
        };
      }

      // Both branches reached here with the body incomplete, so nothing was
      // submitted and it is genuinely safe to tell the user to try again.
      return {
        success: false,
        error: isTimeout
          ? 'Your upload was too slow to finish and timed out. Nothing was submitted, so it is safe to try again — a stronger connection, or smaller photos, will help.'
          : 'We could not upload your documents — the connection kept dropping. Nothing was submitted, so it is safe to try again. If you are on cellular, switching to Wi-Fi usually fixes this.',
      };
    }

    // --- We got an HTTP response ---
    const { status, body: responseText } = outcome;
    const durationMs = Date.now() - startTime;

    if (status < 200 || status >= 300) {
      let errorBody: string;
      let parsedError: Record<string, unknown> | null = null;
      try {
        const json = JSON.parse(responseText);
        parsedError = json;
        errorBody = json.error || json.message || JSON.stringify(json);
      } catch {
        errorBody = responseText || `HTTP ${status}`;
      }

      // 413 is the only status that actually means "too large" — our own function
      // returns it from its size guard. Previously any 500 over 3 MB was reported
      // as a size problem, which sent users off shrinking files to fix upstream
      // outages that had nothing to do with size.
      if (status === 413) {
        const totalMB = (totalFileSize / (1024 * 1024)).toFixed(1);
        const errorMessage = `Your documents are too large to upload (${totalMB} MB total). Please replace them with smaller files and try again.`;
        track('checkout_api_error', {
          ...baseErrorProps(),
          error_message: errorMessage,
          error_type: 'size_limit_server',
          response_status: status,
          duration_ms: durationMs,
          attempt,
        });
        return { success: false, error: errorMessage };
      }

      const errorMessage = `Checkout failed: ${status} ${errorBody}`;
      track('checkout_api_error', {
        ...baseErrorProps(),
        error_message: errorMessage.substring(0, 500),
        error_type: status >= 500 ? 'server_error' : 'validation_error',
        response_status: status,
        response_body: parsedError ?? errorBody?.substring(0, 1000),
        duration_ms: durationMs,
        checkout_name: `${checkoutData.firstName} ${checkoutData.lastName}`,
        attempt,
      });

      // 4xx means the submission itself was rejected. Retrying the same data
      // just fails the same way, so point the user at a human instead.
      if (status >= 400 && status < 500) {
        return {
          success: false,
          needsSupport: true,
          error: 'Your order could not be accepted as submitted. This usually means one of your details or documents needs a quick correction — please call 1-888-300-6961 and we will finish the order with you right away.',
        };
      }

      return {
        success: false,
        error: 'Something went wrong on our end while submitting your order. Nothing was charged or created — please try again in a moment, or call 1-888-300-6961.',
      };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let result: any;
    try {
      result = JSON.parse(responseText);
    } catch {
      // Order succeeded (2xx) but response wasn't parseable JSON
      logger.warn({ area: 'checkout' }, `Checkout returned ${status} with non-JSON body: ${responseText.substring(0, 200)}`);
      result = {};
    }

    const orderId = result.orderId || result.ConfirmationId || result.confirmationId ||
               result.data?.orderId || result.data?.ConfirmationId || result.data?.confirmationId;

    if (!orderId) {
      logger.error({ area: 'checkout' }, `Checkout returned ${status} but no orderId in response: ${responseText.substring(0, 300)}`);
      track('checkout_api_error', {
        ...baseErrorProps(),
        error_message: 'Success response with no orderId',
        error_type: 'missing_order_id',
        response_status: status,
        response_body: responseText.substring(0, 1000),
        attempt,
      });
      return {
        success: false,
        mayHaveSucceeded: true,
        error: 'Your order went through but no confirmation number came back. Please do not submit again — call 1-888-300-6961 and we will look it up for you.',
      };
    }

    track('checkout_api_success', {
      user_id: userId,
      duration_ms: durationMs,
      file_sizes: fileSizes,
      total_file_size: totalFileSize,
      confirmation_id: orderId,
      session_id: sessionId,
      attempts_used: attempt,
    });

    return {
      success: result.success !== false,
      orderId,
      // Electricity completion link - check multiple possible field names
      electricityCompletionLink: result.cpOrderUrl || result.electricityCompletionLink || result.electricityLink ||
               result.data?.cpOrderUrl || result.data?.electricityCompletionLink || result.data?.electricityLink || undefined,
      error: result.error || result.message,
    };
  }

  // Unreachable: the loop either returns or exhausts retries with a return above.
  return { success: false, error: 'Checkout failed. Please try again.' };
}

export async function getOrderStatus(request: OrderStatusRequest): Promise<{
  success: boolean;
  data?: OrderStatusResponse;
  error?: string;
}> {
  const response = await api.post<OrderStatusResponse>(
    '/api/2turniton/order-status',
    request
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: response.data,
    };
  }

  return {
    success: false,
    error: response.error || 'Failed to get order status',
  };
}
